// FacultyAttendanceLog.tsx
//
// Faculty self check-in/out records (ESP32 device + face_auth backed
// arrival/departure), NOT to be confused with AttendanceManagement's
// AttendanceRegistry.tsx (that's classroom lecture registers a faculty
// member has TAKEN of students — a completely different feature that just
// happens to share the "attendance" backend controller).
//
// Mirrors attendanceController.js's getFacultyAttendance/setFacultyAttendance
// exactly: admin/superadmin get every record in the institute (paginated,
// searchable by employee id) with full audit history on any admin
// override; everyone else only ever sees their own records, no search box.
// Same fetch + apiHeaders() pattern as AttendanceRegistry.tsx.

import { useCallback, useEffect, useState } from "react"
import Cookies from "js-cookie"
import {
    Search,
    Loader2,
    ChevronLeft,
    ChevronRight,
    LogIn,
    LogOut,
    ShieldAlert,
    PencilLine,
    History,
    Smartphone,
} from "lucide-react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { Textarea } from "@/components/ui/textarea"
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    CardDescription,
} from "@/components/ui/card"
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog"
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table"

import { environment } from "@/env/env.prod"
import { apiHeaders } from "@/Auth/api-headers."
import { Prevelage } from "../Scripts/rolePrivelageCheck"

// ─────────────────────────────────────────────────────────────────────────
// Types — mirror faculty_attendance's exact columns (see
// attendanceController.js verifyFacultyToken/setFacultyAttendance)
// ─────────────────────────────────────────────────────────────────────────

type FacultyAttendanceRecord = {
    id: number
    faculty_uid: string
    event_date: string
    arrival_epoch: number | null
    departure_epoch: number | null
    source: "device" | "admin_override"
    device_id: string | null
    overridden_by: string | null
    overridden_at: number | null
    override_reason: string | null
}

// One row per change (device event or admin override) that ever touched a
// given (faculty_uid, event_date) — see AttendanceRegisteryFaculty.getHistory.
// Unlike FacultyAttendanceRecord, this is append-only: nothing here is ever
// overwritten by a later change, which is the whole point.
type FacultyAttendanceAuditEntry = {
    id: number
    prev_arrival_epoch: number | null
    prev_departure_epoch: number | null
    prev_source: "device" | "admin_override" | null
    arrival_epoch: number | null
    departure_epoch: number | null
    change_type: "device" | "admin_override"
    changed_by: string | null
    changed_at: number
    reason: string | null
}

const PAGE_SIZE = 20

async function apiGet<T>(path: string, params: Record<string, string | number | undefined>): Promise<T> {
    const query = Object.entries(params)
        .filter(([ , v ]) => v !== undefined && v !== "")
        .map(([ k, v ]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
        .join("&")
    const url = `${environment.BASE_URL}/attendance/${path}${query ? `?${query}` : ""}`
    const res = await fetch(url, { headers: apiHeaders() as HeadersInit })
    const json = await res.json()
    if (!json.success) throw new Error(json.message || "Request failed")
    return json
}

async function apiPost<T>(path: string, body: any): Promise<T> {
    const url = `${environment.BASE_URL}/attendance/${path}`
    const res = await fetch(url, {
        method: "POST",
        headers: apiHeaders() as HeadersInit,
        body: JSON.stringify(body),
    })
    const json = await res.json()
    if (!json.success) throw new Error(json.message || "Request failed")
    return json
}

function formatTime(epochMs: number | null) {
    if (!epochMs) return "—"
    return new Date(epochMs).toLocaleString(undefined, {
        month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    })
}

// Renders "was → now" only when the value actually changed; otherwise just
// the value plain, so an audit entry that only touched departure doesn't
// show a pointless "9:15am → 9:15am" for arrival.
function formatDiff(prev: number | null, next: number | null) {
    if (prev === next) {
        return <span className="text-foreground">{formatTime(next)}</span>
    }
    return (
        <span className="text-foreground">
            <span className="line-through text-muted-foreground/70">{formatTime(prev)}</span>
            {" → "}
            <span className="font-medium">{formatTime(next)}</span>
        </span>
    )
}

export default function FacultyAttendanceLog() {
    const role = Cookies.get("role")
    const canViewAll = new Prevelage(role).canViewAllFacultyAttendance()
    const canOverride = new Prevelage(role).canOverrideFacultyAttendance()

    const [ records, setRecords ] = useState<FacultyAttendanceRecord[]>([])
    const [ total, setTotal ] = useState(0)
    const [ page, setPage ] = useState(1)
    const [ search, setSearch ] = useState("")
    const [ loading, setLoading ] = useState(true)
    const [ error, setError ] = useState<string | null>(null)

    const [ overrideOpen, setOverrideOpen ] = useState(false)
    const [ overrideTarget, setOverrideTarget ] = useState<FacultyAttendanceRecord | null>(null)
    const [ overrideFacultyUid, setOverrideFacultyUid ] = useState("")
    const [ overrideDate, setOverrideDate ] = useState("")
    const [ overrideArrival, setOverrideArrival ] = useState("")
    const [ overrideDeparture, setOverrideDeparture ] = useState("")
    const [ overrideReason, setOverrideReason ] = useState("")
    const [ overrideSaving, setOverrideSaving ] = useState(false)
    const [ overrideError, setOverrideError ] = useState<string | null>(null)

    const [ historyOpen, setHistoryOpen ] = useState(false)
    const [ historyTarget, setHistoryTarget ] = useState<FacultyAttendanceRecord | null>(null)
    const [ historyEntries, setHistoryEntries ] = useState<FacultyAttendanceAuditEntry[]>([])
    const [ historyLoading, setHistoryLoading ] = useState(false)
    const [ historyError, setHistoryError ] = useState<string | null>(null)

    const load = useCallback(async (targetPage: number) => {
        setLoading(true)
        setError(null)
        try {
            const res = await apiGet<{ success: true; records: FacultyAttendanceRecord[]; total: number; page: number; pageSize: number }>(
                "getFacultyAttendance",
                { page: targetPage, pageSize: PAGE_SIZE, search: canViewAll ? search : undefined }
            )
            setRecords(res.records)
            setTotal(res.total)
            setPage(targetPage)
        } catch (e: any) {
            setError(e.message || "Failed to load attendance records")
        } finally {
            setLoading(false)
        }
    }, [ search, canViewAll ])

    useEffect(() => { load(1) }, [ load ])

    const openOverride = (record: FacultyAttendanceRecord | null) => {
        setOverrideTarget(record)
        setOverrideFacultyUid(record?.faculty_uid || "")
        setOverrideDate(record?.event_date || new Date().toISOString().slice(0, 10))
        setOverrideArrival(record?.arrival_epoch ? toLocalInputValue(record.arrival_epoch) : "")
        setOverrideDeparture(record?.departure_epoch ? toLocalInputValue(record.departure_epoch) : "")
        setOverrideReason("")
        setOverrideError(null)
        setOverrideOpen(true)
    }

    const saveOverride = async () => {
        if (!overrideFacultyUid || !overrideDate || !overrideReason.trim()) {
            setOverrideError("Employee ID, date, and a reason are required.")
            return
        }
        setOverrideSaving(true)
        setOverrideError(null)
        try {
            await apiPost("setFacultyAttendance", {
                targetEmployeeId: overrideFacultyUid,
                eventDate: overrideDate,
                arrivalEpoch: overrideArrival ? new Date(overrideArrival).getTime() : null,
                departureEpoch: overrideDeparture ? new Date(overrideDeparture).getTime() : null,
                reason: overrideReason.trim(),
            })
            setOverrideOpen(false)
            await load(page)
        } catch (e: any) {
            setOverrideError(e.message || "Failed to save override")
        } finally {
            setOverrideSaving(false)
        }
    }

    const openHistory = async (record: FacultyAttendanceRecord) => {
        setHistoryTarget(record)
        setHistoryOpen(true)
        setHistoryLoading(true)
        setHistoryError(null)
        setHistoryEntries([])
        try {
            const res = await apiGet<{ success: true; history: FacultyAttendanceAuditEntry[] }>(
                "getFacultyAttendanceHistory",
                { facultyUid: record.faculty_uid, eventDate: record.event_date }
            )
            setHistoryEntries(res.history)
        } catch (e: any) {
            setHistoryError(e.message || "Failed to load history")
        } finally {
            setHistoryLoading(false)
        }
    }

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

    return (
        <div className="min-h-screen bg-[oklch(0.97_0.01_90)] p-6 md:p-8">
            <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight text-foreground">Faculty Attendance</h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                        {canViewAll
                            ? "Every faculty check-in/out this institute has recorded, device-verified or admin-corrected."
                            : "Your own check-in/out history from the attendance device."}
                    </p>
                </div>
                {canOverride && (
                    <Button className="gap-2" onClick={() => openOverride(null)}>
                        <PencilLine className="h-4 w-4" /> Add / Correct Record
                    </Button>
                )}
            </div>

            {canViewAll && (
                <Card className="mb-6 border border-border/60 shadow-sm">
                    <CardContent className="pt-6">
                        <div className="space-y-2 max-w-sm">
                            <Label>Search by employee ID</Label>
                            <div className="relative">
                                <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                                <Input
                                    placeholder="Employee ID..."
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                    className="pl-8"
                                />
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            <Card className="border border-border/60 shadow-sm">
                <CardHeader>
                    <CardTitle className="text-base font-semibold">Records</CardTitle>
                    <CardDescription>{total} record{total === 1 ? "" : "s"}</CardDescription>
                </CardHeader>
                <CardContent>
                    {error && <p className="text-sm text-red-600 mb-4">{error}</p>}

                    <div className="rounded-lg border border-border/60 overflow-hidden">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    {canViewAll && <TableHead>Employee</TableHead>}
                                    <TableHead>Date</TableHead>
                                    <TableHead><LogIn className="h-3.5 w-3.5 inline mr-1" />Arrival</TableHead>
                                    <TableHead><LogOut className="h-3.5 w-3.5 inline mr-1" />Departure</TableHead>
                                    <TableHead>Source</TableHead>
                                    <TableHead className="text-right">Action</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {loading && (
                                    <TableRow>
                                        <TableCell colSpan={canViewAll ? 6 : 5} className="text-center text-sm text-muted-foreground py-8">
                                            Loading…
                                        </TableCell>
                                    </TableRow>
                                )}
                                {!loading && records.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={canViewAll ? 6 : 5} className="text-center text-sm text-muted-foreground py-8">
                                            No attendance records yet.
                                        </TableCell>
                                    </TableRow>
                                )}
                                {!loading && records.map(r => (
                                    <TableRow key={r.id}>
                                        {canViewAll && <TableCell className="font-medium">{r.faculty_uid}</TableCell>}
                                        <TableCell>{r.event_date}</TableCell>
                                        <TableCell>{formatTime(r.arrival_epoch)}</TableCell>
                                        <TableCell>{formatTime(r.departure_epoch)}</TableCell>
                                        <TableCell>
                                            {r.source === "device" ? (
                                                <Badge variant="outline" className="text-emerald-600 bg-emerald-50 border-emerald-200">
                                                    Device
                                                </Badge>
                                            ) : (
                                                <div className="flex flex-col gap-1">
                                                    <Badge variant="outline" className="text-amber-600 bg-amber-50 border-amber-200 w-fit gap-1">
                                                        <ShieldAlert className="h-3 w-3" /> Admin override
                                                    </Badge>
                                                    <span className="text-[11px] text-muted-foreground">
                                                        by {r.overridden_by || "—"} · {formatTime(r.overridden_at)}
                                                        {r.override_reason ? ` · "${r.override_reason}"` : ""}
                                                    </span>
                                                </div>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex justify-end gap-2">
                                                <Button variant="outline" size="sm" className="gap-1" onClick={() => openHistory(r)}>
                                                    <History className="h-3.5 w-3.5" /> History
                                                </Button>
                                                {canOverride && (
                                                    <Button variant="outline" size="sm" className="gap-1" onClick={() => openOverride(r)}>
                                                        <PencilLine className="h-3.5 w-3.5" /> Correct
                                                    </Button>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>

                    <div className="flex items-center justify-between mt-4">
                        <p className="text-xs text-muted-foreground">Page {page} of {totalPages}</p>
                        <div className="flex gap-2">
                            <Button variant="outline" size="sm" disabled={page <= 1 || loading} onClick={() => load(page - 1)} className="gap-1">
                                <ChevronLeft className="h-3.5 w-3.5" /> Prev
                            </Button>
                            <Button variant="outline" size="sm" disabled={page >= totalPages || loading} onClick={() => load(page + 1)} className="gap-1">
                                Next <ChevronRight className="h-3.5 w-3.5" />
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* OVERRIDE DIALOG — admin/superadmin only, upserts on
                (faculty_uid, institute_code, event_date), so this doubles as
                "add a missing record" and "correct an existing one". */}
            <Dialog open={overrideOpen} onOpenChange={setOverrideOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>{overrideTarget ? "Correct attendance record" : "Add attendance record"}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="space-y-2">
                            <Label>Employee ID</Label>
                            <Input
                                value={overrideFacultyUid}
                                onChange={e => setOverrideFacultyUid(e.target.value)}
                                disabled={!!overrideTarget}
                                placeholder="e.g. FAC1023"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Date</Label>
                            <Input type="date" value={overrideDate} onChange={e => setOverrideDate(e.target.value)} disabled={!!overrideTarget} />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-2">
                                <Label>Arrival</Label>
                                <Input type="datetime-local" value={overrideArrival} onChange={e => setOverrideArrival(e.target.value)} />
                            </div>
                            <div className="space-y-2">
                                <Label>Departure</Label>
                                <Input type="datetime-local" value={overrideDeparture} onChange={e => setOverrideDeparture(e.target.value)} />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>Reason <span className="text-muted-foreground font-normal">(required — kept as an audit note)</span></Label>
                            <Textarea
                                value={overrideReason}
                                onChange={e => setOverrideReason(e.target.value)}
                                placeholder="e.g. Forgot phone at home, verified in person"
                                rows={3}
                            />
                        </div>
                        {overrideError && <p className="text-sm text-red-600">{overrideError}</p>}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOverrideOpen(false)} disabled={overrideSaving}>Cancel</Button>
                        <Button onClick={saveOverride} disabled={overrideSaving} className="gap-2">
                            {overrideSaving && <Loader2 className="h-4 w-4 animate-spin" />}
                            Save
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* HISTORY DIALOG — the full append-only audit trail for one day,
                most recent first. This is where every past correction's
                reason actually survives; the table row above only ever
                shows the latest values. */}
            <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
                <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>
                            History — {historyTarget?.faculty_uid} · {historyTarget?.event_date}
                        </DialogTitle>
                    </DialogHeader>

                    {historyLoading && <p className="text-sm text-muted-foreground py-6 text-center">Loading…</p>}
                    {historyError && <p className="text-sm text-red-600 py-6 text-center">{historyError}</p>}

                    {!historyLoading && !historyError && (
                        <div className="space-y-3">
                            {historyEntries.length === 0 && (
                                <p className="text-sm text-muted-foreground text-center py-6">No history recorded for this day.</p>
                            )}
                            {historyEntries.map(entry => (
                                <div key={entry.id} className="rounded-lg border border-border/60 p-3">
                                    <div className="flex items-center justify-between mb-1.5">
                                        {entry.change_type === "device" ? (
                                            <Badge variant="outline" className="text-emerald-600 bg-emerald-50 border-emerald-200 gap-1">
                                                <Smartphone className="h-3 w-3" /> Device
                                            </Badge>
                                        ) : (
                                            <Badge variant="outline" className="text-amber-600 bg-amber-50 border-amber-200 gap-1">
                                                <ShieldAlert className="h-3 w-3" /> Admin override
                                            </Badge>
                                        )}
                                        <span className="text-xs text-muted-foreground">{formatTime(entry.changed_at)}</span>
                                    </div>
                                    <div className="text-sm grid grid-cols-2 gap-x-4 gap-y-1">
                                        <span className="text-muted-foreground">
                                            Arrival: {formatDiff(entry.prev_arrival_epoch, entry.arrival_epoch)}
                                        </span>
                                        <span className="text-muted-foreground">
                                            Departure: {formatDiff(entry.prev_departure_epoch, entry.departure_epoch)}
                                        </span>
                                    </div>
                                    <div className="text-xs text-muted-foreground mt-1.5">
                                        by {entry.changed_by || "—"}
                                        {entry.reason && <> · &quot;{entry.reason}&quot;</>}
                                        {entry.prev_source === null && <> · first record for this day</>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    )
}

function toLocalInputValue(epochMs: number) {
    const d = new Date(epochMs)
    const pad = (n: number) => String(n).padStart(2, "0")
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}
